import { useSyncExternalStore } from "react";

export type ThemePreference = "system" | "light" | "dark";
const KEY = "nextsub.theme";
const listeners = new Set<() => void>();

function read(): ThemePreference {
  try {
    const v = localStorage.getItem(KEY);
    return v === "light" || v === "dark" ? v : "system";
  } catch {
    return "system";
  }
}

let preference: ThemePreference = typeof window === "undefined" ? "system" : read();
const media = typeof window !== "undefined" ? window.matchMedia("(prefers-color-scheme: dark)") : null;

interface ThemeSnapshot {
  preference: ThemePreference;
  dark: boolean;
}

function resolveDark(): boolean {
  return preference === "dark" || (preference === "system" && !!media?.matches);
}

// Stable object per (preference, resolved appearance) so useSyncExternalStore
// notices OS appearance changes while "system" is selected.
let snapshot: ThemeSnapshot = { preference, dark: resolveDark() };

function apply() {
  const dark = resolveDark();
  document.documentElement.classList.toggle("dark", dark);
  if (snapshot.preference !== preference || snapshot.dark !== dark) snapshot = { preference, dark };
  for (const l of listeners) l();
}

media?.addEventListener("change", () => preference === "system" && apply());

export function setTheme(next: ThemePreference) {
  preference = next;
  try {
    if (next === "system") localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, next);
  } catch {
    /* ignore */
  }
  apply();
}

export function isDark(): boolean {
  return typeof document !== "undefined" && document.documentElement.classList.contains("dark");
}

export function useTheme() {
  const current = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => snapshot,
    () => snapshot,
  );
  return { preference: current.preference, setTheme, dark: current.dark };
}
